import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseConfig';
import hybridService from '../services/hybridService';
import LoadingSpinner from '../components/common/LoadingSpinner';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
                setCurrentUser(session.user);
                fetchUserRole(session.user.id);
            } else {
                setLoading(false);
            }
        }).catch(err => {
            console.error('Session fetch error:', err);
            setLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                if (session?.user) {
                    setCurrentUser(session.user);
                    await fetchUserRole(session.user.id);
                } else {
                    setCurrentUser(null);
                    setUserRole(null);
                }
                setLoading(false);
            }
        );

        // Safety timeout to prevent infinite loading
        const timer = setTimeout(() => {
            console.warn('Auth timeout - Forcing app load');
            setLoading(false);
        }, 5000);

        return () => {
            subscription.unsubscribe();
            clearTimeout(timer);
        };
    }, []);

    const fetchUserRole = async (userId) => {
        try {
            const { data, error } = await hybridService.getUserProfile(userId);
            
            if (data && !error) {
                setUserRole(data.role);
            } else {
                setUserRole('student'); // Default
            }
        } catch (error) {
            console.error("Error fetching user role:", error);
            setUserRole('student');
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        if (error) throw error;
        return data;
    };

    const signup = async (email, password, role = 'student', additionalData = {}) => {
        // Create auth user
        const { data, error } = await supabase.auth.signUp({
            email,
            password
        });

        if (error) throw error;

        const user = data.user;

        // Set role immediately for UI
        setUserRole(role);

        // Create user profile in MongoDB
        if (user) {
            const { error: profileError } = await hybridService.signUp(email, password, {
                id: user.id,
                email: user.email,
                role: role,
                created_at: new Date().toISOString(),
                ...additionalData
            });

            if (profileError) {
                console.error("Profile creation failed:", profileError);
            }
        }

        return data;
    };

    const logout = async () => {
        try {
            await supabase.auth.signOut();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Explicitly clear state
            setCurrentUser(null);
            setUserRole(null);

            // Aggressively clear all Supabase-related items from local storage
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key);
                }
            });

            // Force hard redirect to login page to clear memory/state
            window.location.href = '/login';
        }
    };

    // Auto Logout Logic
    useEffect(() => {
        const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 minutes
        let inactivityTimer;

        const resetTimer = () => {
            if (currentUser) {
                clearTimeout(inactivityTimer);
                inactivityTimer = setTimeout(() => {
                    console.log('User inactive for 20 minutes. Logging out...');
                    logout();
                }, INACTIVITY_LIMIT);
            }
        };

        const handleActivity = () => {
            resetTimer();
        };

        if (currentUser) {
            // Initial start
            resetTimer();

            // Listen for user activity
            window.addEventListener('mousemove', handleActivity);
            window.addEventListener('keypress', handleActivity);
            window.addEventListener('click', handleActivity);
            window.addEventListener('scroll', handleActivity);
            window.addEventListener('touchstart', handleActivity);
        }

        return () => {
            clearTimeout(inactivityTimer);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keypress', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            window.removeEventListener('touchstart', handleActivity);
        };
    }, [currentUser]);

    const value = {
        currentUser,
        userRole,
        login,
        signup,
        logout,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {loading ? <LoadingSpinner fullScreen /> : children}
        </AuthContext.Provider>
    );
};
